import { ListResponseModel, toQueryString } from ".";
import {
	AdmimCustomerDetailsModel,
	AdminCustomerModel,
	CustomerDashboardModel,
	CustomerPaymentOptions,
	CustomerQuery,
	CustomerType,
} from "../model/customer/customer";
import { LoanDetails, FulfilledLoans, OrderQuery } from "../model/orders/orders";
import { CustomerPaymentHistory } from "../model/payments/payments";
import { apiSlice } from "../stores/base/api-slice";
import { updateUsername } from "../stores/slices/current-user-slice";

const customerDashboardEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getCustomerDashboardInformation: builder.query<CustomerDashboardModel, void>({
			providesTags: ["CustomerDashboard"],
			query: () => ({
				url: "/customer/dashboard",
				method: "GET",
			}),
		}),
		getTransactions: builder.query<ListResponseModel<Array<CustomerPaymentHistory>>, CustomerQuery>({
			providesTags: ["Transactions"],
			query: query => ({
				url: "/customer/transactions" + toQueryString(query),
				method: "GET",
			}),
		}),
		getCustomer: builder.query<ListResponseModel<Array<AdminCustomerModel>>, CustomerQuery>({
			query: query => ({
				url: "/customer/page" + toQueryString(query),
				method: "GET",
			}),
		}),
		getCustomerDetail: builder.query<AdmimCustomerDetailsModel, string>({
			query: customerId => ({
				url: `/customer/${customerId}/details`,
				method: "GET",
			}),
		}),
		getCustomerFulfilledLoans: builder.query<ListResponseModel<Array<FulfilledLoans>>, OrderQuery>({
			query: query => ({
				url: `/customer/${query?.customerId}/details/fulfilled-loans` + toQueryString({ Page: query?.Page, PageSize: query?.PageSize }),
				method: "GET",
			}),
		}),
		getCustomerActiveLoanDetails: builder.query<LoanDetails, string>({
			query: customerId => ({
				url: `/customer/${customerId}/details/active-loan-details`,
				method: "GET",
			}),
		}),
		getCustomerLoanRepaymentDetails: builder.query<ListResponseModel<Array<CustomerPaymentHistory>>, OrderQuery>({
			query: query => ({
				url: `/customer/${query?.customerId}/details/repayments` + toQueryString({ Page: query?.Page, PageSize: query?.PageSize }),
				method: "GET",
			}),
		}),
		getCustomerOnboardingStatus: builder.query<Array<{ type: string; date: string }>, string>({
			query: customerId => ({
				url: `/customer/${customerId}/details/onboarding-statuses`,
				method: "GET",
			}),
		}),
		getCustomerMandates: builder.query<
			{
				cardPaymentStatus: string;
				directDebitMandateStatus: string;
				accountBalance: number;
				accountBalanceUpdatedAt: string;
			},
			string
		>({
			query: customerId => ({
				url: `/customer/${customerId}/details/onboarding-statuses`,
				method: "GET",
			}),
		}),
		getCustomerMandatesV2: builder.query<
			{
				cardPaymentStatus: string;
				directDebitMandateStatus: string;
				accountBalance: number;
				accountBalanceUpdatedAt: string;
			},
			string
		>({
			query: customerId => ({
				url: `/customer/${customerId}/details/debits-mandate`,
				method: "GET",
			}),
		}),
		initiateSignup: builder.mutation<{ customerSignupId: string }, { email: string; firstName: string; lastName: string; password: string }>({
			query: request => ({
				url: `/customer/v2/registration/initiate`,
				method: "POST",
				body: request,
			}),
		}),
		verifyOtp: builder.mutation<{ token: string; userType: string; refreshToken: string }, { customerSignupId: string; otp: string }>({
			query: request => ({
				url: `/customer/v2/registration/verify`,
				method: "POST",
				body: request,
			}),
		}),
		resendOtp: builder.mutation<void, { customerSignupId: string }>({
			query: request => ({
				url: `/customer/v2/registration/resend`,
				method: "POST",
				body: request,
			}),
		}),
		getCustomerInfo: builder.query<CustomerType, void>({
			providesTags: ["CustomerInfo"],
			query: () => ({
				url: "/customer/info",
				method: "GET",
			}),
			onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
				try {
					const { data } = await queryFulfilled;
					dispatch(updateUsername({userName :`${data?.firstName} ${data?.lastName ?? ""}`}));
				} catch (error) {
					console.error("Error fetching customer info:", error);
				}
			},
		}),
		getCustomerPaymentOptions: builder.query<CustomerPaymentOptions, void>({
			query: () => ({
				url: "/customer/payment-options",
				method: "GET",
			}),
		}),
	}),
});

export const {
	useLazyGetCustomerDashboardInformationQuery,
	useLazyGetCustomerQuery,
	useLazyGetTransactionsQuery,
	useLazyGetCustomerDetailQuery,
	useLazyGetCustomerActiveLoanDetailsQuery,
	useLazyGetCustomerLoanRepaymentDetailsQuery,
	useLazyGetCustomerFulfilledLoansQuery,
	useLazyGetCustomerOnboardingStatusQuery,
	useLazyGetCustomerMandatesQuery,
	useLazyGetCustomerMandatesV2Query,
	useLazyGetCustomerInfoQuery,
	useInitiateSignupMutation,
	useVerifyOtpMutation,
	useResendOtpMutation,
	useLazyGetCustomerPaymentOptionsQuery,
} = customerDashboardEndpoints;
